import React, { useState } from 'react';
import { Guitar, BookOpen, Target } from 'lucide-react';
import { ChordStrummer } from './guitar/ChordStrummer';
import { FretboardGame } from './guitar/FretboardGame';
import { soundEngine } from '../utils/audio';

export const GuitarAcademy: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'fretboard' | 'chords'>('fretboard');

  const tabs = [
    { id: 'fretboard', label: 'Free Fretboard Explorer & Notes', icon: Target, tag: 'Interactive Explorer' },
    { id: 'chords', label: 'Chord Garden & Strummer', icon: BookOpen, tag: 'Interactive Strum' },
  ];

  return (
    <section id="guitar" className="py-16 md:py-24 bg-gradient-to-b from-purple-50/40 via-white to-pink-50/50 relative">
      
      {/* Kawaii Decorative Elements */}
      <div className="absolute top-12 left-10 text-3xl animate-float select-none pointer-events-none opacity-60">🎸</div>
      <div className="absolute top-36 right-10 text-3xl animate-float-delay select-none pointer-events-none opacity-60">🎶</div>
      <div className="absolute bottom-16 right-16 text-3xl animate-float select-none pointer-events-none opacity-60">⭐</div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 space-y-3">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-100 text-purple-700 font-bold text-xs">
            <Guitar className="w-4 h-4" />
            <span>Interactive Music Academy</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800">
            Learn <span className="bg-gradient-to-r from-purple-600 via-pink-500 to-rose-500 bg-clip-text text-transparent">Guitar Notes & Chords</span>
          </h2>
          <p className="text-gray-600 text-base sm:text-lg">
            Pick your strings, discover the magic behind each chord, and level up your musical ear with kawaii mini-games!
          </p>
        </div>

        {/* Tab switcher buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  soundEngine.playPopSfx();
                  setActiveSubTab(tab.id as 'chords' | 'fretboard');
                }}
                className={`px-5 py-3 rounded-2xl font-bold text-sm sm:text-base flex items-center gap-2.5 transition-all cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-r from-purple-600 via-pink-500 to-rose-500 text-white shadow-lg shadow-pink-200/60 scale-105'
                    : 'bg-white text-gray-700 border-2 border-purple-100 hover:border-pink-300 hover:bg-pink-50/50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                  isActive ? 'bg-white/20 text-white' : 'bg-purple-100 text-purple-800'
                }`}>
                  {tab.tag}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        <div className="transition-all">
          {activeSubTab === 'fretboard' && <FretboardGame />}
          {activeSubTab === 'chords' && <ChordStrummer />}
        </div>

      </div>
    </section>
  );
};
